import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CubeState, FlyControl, Room } from './scene/room';
import { glideTo, holdProgress, spreadAt, spreadScrollY, startCardMotion, startSmoothWheel, stop, stopScrollY } from './layout';
import AsteriskMark from './AsteriskMark';
import Loader from './Loader';
import NoWebGL from './NoWebGL';
import Arcade from './arcade/Arcade';
import { GAMES, loadBest } from './arcade/game';
import { fontsReady } from './fonts';
import { hasWebGL } from './webgl';
import { CV, EMAIL, FACTS, INTRO, JOBS, LANGS, LINKEDIN, PHONE, PROJECTS, SKILLS, type FactId } from './data';

const SPREADS = PROJECTS.length + 1;
const BOOK_STOP = 2;
const EXPLORE_STOP = 4;
// Finding this many fun facts unlocks the arcade.
const UNLOCK_AT = 7;
// Big enough screens show experience, toolkit and contact on the monitor, laptop and phone in the room;
// smaller ones keep them as cards.
const SCREENS_MQ = '(min-width: 900px) and (min-height: 560px) and (min-aspect-ratio: 1/1)';
const FOUND_KEY = 'room-found';
const REAL_KEY = 'drone-realistic';
// Dark mode is only ever a choice: the system setting is never read. index.html applies it before first paint.
const THEME_KEY = 'theme';
// Realistic flying needs a keyboard; touch screens always get the easy mode and its buttons.
const TOUCH = typeof matchMedia !== 'undefined' && matchMedia('(hover: none) and (pointer: coarse)').matches;
const UI_FACES = ['400 1em "Young Serif"', '400 1em "Hanken Grotesk"', '600 1em "Hanken Grotesk"', '600 1em "JetBrains Mono"'];

// Header links and the camera stop each one points at; the intro (stop 0) has no link.
const NAV = [
  { href: '#experience', label: 'Experience', stop: 1 },
  { href: '#projects', label: 'Projects', stop: 2 },
  { href: '#skills', label: 'Toolkit', stop: 3 },
  { href: '#fun-facts', label: 'Fun facts', stop: 4 },
  { href: '#contact', label: 'Contact', stop: 5 },
  { href: '#arcade', label: 'Arcade', stop: 6 },
];

function loadDark() {
  try { return localStorage.getItem(THEME_KEY) === 'dark'; } catch { return false; }
}

function loadRealistic() {
  try { return !TOUCH && localStorage.getItem(REAL_KEY) === '1'; } catch { return false; }
}

// Throttle bar for realistic mode, read from the room every frame; the notch marks hover.
function ThrottleGauge({ room }: { room: React.RefObject<Room | null> }) {
  const fill = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const t = room.current?.throttle() ?? 0;
      if (fill.current) fill.current.style.transform = `scaleX(${t.toFixed(3)})`;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [room]);
  return (
    <div className="fly-gauge" aria-hidden="true">
      <span className="meta">Throttle</span>
      <span className="fly-gauge-bar"><span ref={fill} /></span>
    </div>
  );
}

function loadFound(): Set<FactId> {
  try { return new Set(JSON.parse(localStorage.getItem(FOUND_KEY) || '[]')); } catch { return new Set(); }
}

const MARKERS = [
  { color: 'var(--hive-red)', shape: 'circle' },
  { color: 'var(--hive-yellow)', shape: 'square' },
  { color: 'var(--hive-blue)', shape: 'triangle' },
];

// A section's card. With `surface` set (screens mode), it's drawn onto that object in the room instead.
function Card({ side, accent, surface, fold, still, className, children }: { side: -1 | 1; accent: string; surface?: string; fold?: boolean; still?: boolean; className?: string; children: React.ReactNode }) {
  return (
    <div
      className={['card', surface && `surface surface-${surface}`, className].filter(Boolean).join(' ')}
      data-card={surface ? undefined : ''} data-surface={surface} data-fold={fold ? '' : undefined} data-still={still ? '' : undefined} data-side={side}
      style={{ '--accent': `var(--hive-${accent})` } as React.CSSProperties}
    >
      {/* Laid out at its design size and zoomed up, so the room scales it down onto the object crisply. */}
      {surface ? <div className="surface-inner">{children}</div> : children}
    </div>
  );
}

// The title bar a section gets when it's shown on a screen in the room (hidden on cards).
function SurfaceBar({ title }: { title: string }) {
  return (
    <div className="surface-bar" aria-hidden="true">
      <i /><i /><i /><span>{title}</span>
    </div>
  );
}

// Experience: the jobs list, shown in the monitor's browser or as a card.
function Experience() {
  return (
    <>
      <span className="eyebrow">Experience</span>
      <h2>Where I've worked</h2>
      <ol className="jobs" style={{ '--rows': Math.ceil(JOBS.length / 2) } as React.CSSProperties}>
        {JOBS.map((j, i) => {
          const m = MARKERS[i % 3];
          return (
            <li key={j.company + j.span}>
              <span className={`marker marker-${m.shape}`} style={{ background: m.color }} />
              <div>
                <span className="meta muted">{j.span}</span>
                <h3>{j.company}</h3>
                <span className="role">{j.role} · {j.loc}</span>
              </div>
            </li>
          );
        })}
      </ol>
    </>
  );
}

// The monitor in screens mode: a lock screen first, then a browser window with the experience page in it.
function Computer({ clock, children }: { clock: string; children: React.ReactNode }) {
  const [state, setState] = useState<'locked' | 'opening' | 'open'>('locked');
  const browser = useRef<HTMLDivElement>(null);
  const open = () => {
    if (state !== 'locked') return;
    setState('opening');
    setTimeout(() => setState('open'), matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 900);
  };
  useEffect(() => { if (state === 'open') browser.current?.focus({ preventScroll: true }); }, [state]);
  const date = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  return state !== 'open' ? (
    <div className={`pc-lock${state === 'opening' ? ' is-opening' : ''}`}>
      <div className="pc-wall" aria-hidden="true"><i /><i /><i /></div>
      <div className="pc-time" aria-hidden="true"><strong>{clock}</strong><span>{date}</span></div>
      <div className="pc-login">
        <span className="pc-avatar" aria-hidden="true"><AsteriskMark size={40} /></span>
        <span className="pc-user">Isak Gerre</span>
        <span className="pc-pass" aria-hidden="true">{[0, 1, 2, 3, 4, 5].map(i => <i key={i} style={{ '--i': i } as React.CSSProperties} />)}</span>
        <button type="button" className="pc-open" onClick={open} aria-label="Open the computer to see my experience">
          {/* Both labels share one cell, so the button keeps its width when the label swaps. */}
          <span className="pc-open-label">
            <span hidden={state === 'opening'}>Press to open</span>
            <span hidden={state !== 'opening'}>Logging in…</span>
          </span>
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </div>
  ) : (
    <div ref={browser} className="pc-browser" tabIndex={-1}>
      <div className="pc-tabs">
        <i /><i /><i />
        <span className="pc-tab is-on" aria-hidden="true"><AsteriskMark size={12} />Experience · Isak Gerre</span>
        {/* The second tab is the real CV. */}
        <a className="pc-tab" href={CV} download>CV_Isak_Gerre.pdf ↓</a>
      </div>
      <div className="pc-bar">
        <span className="pc-nav" aria-hidden="true">← → ↻</span>
        <span className="pc-url"><span aria-hidden="true">🔒︎ </span>isakgerre.se/<strong>experience</strong></span>
        <button type="button" className="pc-lockbtn" onClick={() => setState('locked')}>Lock ⏻</button>
      </div>
      <div className="pc-page">{children}</div>
    </div>
  );
}

function useClock() {
  const fmt = () => new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const [t, setT] = useState(fmt);
  useEffect(() => { const id = setInterval(() => setT(fmt()), 15000); return () => clearInterval(id); }, []);
  return t;
}

function loadScreens() {
  return typeof matchMedia !== 'undefined' && matchMedia(SCREENS_MQ).matches;
}

// A project description that phones cut to a few lines, with a toggle to read the rest when it's cut.
function ProjectText({ text }: { text: string }) {
  const ref = useRef<HTMLParagraphElement>(null);
  const [open, setOpen] = useState(false);
  const [cut, setCut] = useState(false);
  useLayoutEffect(() => {
    const p = ref.current;
    if (!p) return;
    const check = () => setCut(p.scrollHeight > p.clientHeight + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(p);
    return () => ro.disconnect();
  }, []);
  return (
    <>
      <p ref={ref} className={open ? 'is-open' : undefined}>{text}</p>
      {(cut || open) && (
        <button type="button" className="more" aria-expanded={open} onClick={() => setOpen(o => !o)}>
          {open ? 'Show less' : 'Read more'}
        </button>
      )}
    </>
  );
}

// Hold-to-fly touch button: pressed while a finger is on it, released when it lifts or slides off.
function FlyButton({ room, control, label, children }: { room: React.RefObject<Room | null>; control: FlyControl; label: string; children: React.ReactNode }) {
  const set = (down: boolean) => room.current?.flyInput(control, down);
  return (
    <button
      type="button" aria-label={label} className={`fly-btn fly-${control}`}
      onPointerDown={e => { e.currentTarget.setPointerCapture(e.pointerId); set(true); }}
      onPointerUp={() => set(false)} onPointerCancel={() => set(false)} onLostPointerCapture={() => set(false)}
      onContextMenu={e => e.preventDefault()}
    >
      <span aria-hidden="true">{children}</span>
    </button>
  );
}

export default function App() {
  const hostRef = useRef<HTMLDivElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const roomRef = useRef<Room | null>(null);
  const [picked, setPicked] = useState<FactId | null>(null);
  const [found, setFound] = useState(loadFound);
  const [spread, setSpread] = useState(0);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [atTop, setAtTop] = useState(true);
  const [arcade, setArcade] = useState(false);
  const [factsMin, setFactsMin] = useState(false);
  const [flying, setFlying] = useState(false);
  const [realistic, setRealistic] = useState(loadRealistic);
  const [cube, setCube] = useState<CubeState | null>(null);
  // False once it's clear there's no 3D room (no WebGL, or the scene failed to load).
  const [has3d, setHas3d] = useState(true);
  const [wide, setWide] = useState(loadScreens);
  const screens = wide && has3d;
  // Near the explore stop, the only place the room's objects can be clicked.
  const [exploring, setExploring] = useState(false);
  // Bumped when the locked cabinet is clicked, to shake the lock note.
  const [nudge, setNudge] = useState(0);
  const foundRef = useRef(0);
  const clock = useClock();
  const navRef = useRef<HTMLDivElement>(null);
  const markRef = useRef<HTMLSpanElement>(null);
  const [navAt, setNavAt] = useState(-1);
  // WebGL notice: open on the first screen, a header pill once the visitor scrolls on, null once dismissed.
  const [glNote, setGlNote] = useState<'open' | 'pill' | null>(null);
  const [dark, setDark] = useState(loadDark);
  const darkRef = useRef(dark);
  useEffect(() => { if (!atTop) setGlNote(n => (n === 'open' ? 'pill' : n)); }, [atTop]);

  useEffect(() => startCardMotion(), []);

  useEffect(() => {
    const mq = matchMedia(SCREENS_MQ);
    const on = () => setWide(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  useEffect(() => {
    document.documentElement.classList.toggle('is-screens', screens);
    roomRef.current?.setScreens(screens);
  }, [screens, ready]);
  useEffect(() => startSmoothWheel(), []);

  useEffect(() => {
    const onScroll = () => setAtTop(scrollY < 40);
    addEventListener('scroll', onScroll, { passive: true });
    return () => removeEventListener('scroll', onScroll);
  }, []);

  // In-page links glide to where their section's card is in view, instead of jumping to the section top.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as Element | null)?.closest?.<HTMLAnchorElement>('a[href^="#"]');
      const el = a && document.getElementById(a.hash.slice(1));
      if (!el) return;
      e.preventDefault();
      const stop = el.dataset.stop;
      const y = stop !== undefined ? stopScrollY(+stop) : null;
      glideTo(y ?? el.getBoundingClientRect().top + scrollY);
    };
    addEventListener('click', onClick);
    return () => removeEventListener('click', onClick);
  }, []);

  // Which sketchbook spread is open, for the caption card.
  useEffect(() => {
    let raf = 0, cur = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const n = spreadAt(holdProgress(BOOK_STOP), SPREADS).current;
      if (n !== cur) { cur = n; setSpread(n); }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // The nav underline sits under the current link, exactly as wide as its word, and glides (in CSS) when the link changes.
  useEffect(() => {
    let raf = 0, cur = -2, last = '';
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const nav = navRef.current, mark = markRef.current;
      if (!nav || !mark) return;
      const links = nav.querySelectorAll<HTMLElement>('a');
      const s = stop();
      setExploring(Math.abs(s - EXPLORE_STOP) < 0.4);
      const on = s < NAV[0].stop - 0.5 ? -1 : NAV.reduce((m, n, i) => (Math.abs(n.stop - s) < Math.abs(NAV[m].stop - s) ? i : m), 0);
      // On the intro the underline waits, shrunk to nothing, at the first link's left edge.
      const l = links[Math.max(0, on)];
      if (on !== cur) {
        cur = on; setNavAt(on);
        // On phones the link bar scrolls sideways; keep the current link in view.
        if (on >= 0 && nav.scrollWidth > nav.clientWidth) nav.scrollTo({ left: l.offsetLeft - (nav.clientWidth - l.offsetWidth) / 2, behavior: 'smooth' });
      }
      // A link's box includes the letter spacing after its last letter; the underline stops at the letter.
      const w = on < 0 ? 0 : l.offsetWidth - (parseFloat(getComputedStyle(l).letterSpacing) || 0);
      const css = `translateX(${l.offsetLeft}px) scaleX(${w.toFixed(1)})`;
      if (css !== last) { last = css; mark.style.transform = css; }
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const sendScores = () => roomRef.current?.setArcadeScores(GAMES.map(g => ({ title: g.title, best: loadBest(g.id) })));

  // The camera zooms in on the cabinet while the game screen, glued to its glass, rides along.
  // Locked until enough facts are found; the cabinet's click then just shakes the lock note.
  const openArcade = () => {
    if (foundRef.current < UNLOCK_AT) { setNudge(n => n + 1); return; }
    document.documentElement.classList.add('is-arcade');
    roomRef.current?.focusArcade(true);
    setArcade(true);
  };
  const closeArcade = () => {
    setArcade(false);
    document.documentElement.classList.remove('is-arcade');
    roomRef.current?.focusArcade(false);
    sendScores();
  };
  // Returns false without a 3D room, so the arcade screen floats centered instead.
  const attachScreen = useCallback((el: HTMLElement | null) => roomRef.current?.setArcadeScreen(el) ?? false, []);

  // While the drone is flown, the page steps aside like it does for the arcade.
  const onFly = (on: boolean) => {
    document.documentElement.classList.toggle('is-flying', on);
    setFlying(on);
  };

  // Solving the cube, the page steps aside the same way.
  const onCube = (state: CubeState | null) => {
    document.documentElement.classList.toggle('is-cube', !!state);
    setCube(state);
  };

  useEffect(() => {
    darkRef.current = dark;
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
    roomRef.current?.setNight(dark);
    try { localStorage.setItem(THEME_KEY, dark ? 'dark' : 'light'); } catch { /* storage unavailable */ }
  }, [dark, ready]);

  useEffect(() => {
    roomRef.current?.setRealistic(realistic);
    try { localStorage.setItem(REAL_KEY, realistic ? '1' : '0'); } catch { /* storage unavailable */ }
  }, [realistic, ready]);

  const pick = (id: FactId | null) => {
    setPicked(id);
    if (id) setFound(f => (f.has(id) ? f : new Set(f).add(id)));
  };

  useEffect(() => {
    let dead = false;
    // Without WebGL there's no room to load; the page still reads fine and a notice says how to turn it on.
    const gl = hasWebGL();
    if (!gl) { setGlNote('open'); setHas3d(false); }
    const scene = !gl ? Promise.resolve() : import('./scene/room')
      .then(({ createRoom }) => {
        if (dead || !hostRef.current || !bubbleRef.current) return;
        roomRef.current = createRoom(hostRef.current, { projects: PROJECTS, bubble: bubbleRef.current, onPick: pick, onArcade: openArcade, onSpread: j => goToSpread(j), onFly, onCube, onLights: () => setDark(d => !d), night: darkRef.current });
        sendScores();
      })
      .catch(e => { console.warn('3D scene failed to load', e); if (!dead) setHas3d(false); });
    // The loader waits for the scene and the web fonts; a failed scene still lets it finish.
    Promise.all([scene, fontsReady(UI_FACES)]).finally(() => { if (!dead) setReady(true); });
    return () => { dead = true; roomRef.current?.dispose(); roomRef.current = null; };
  }, []);

  useEffect(() => {
    foundRef.current = found.size;
    roomRef.current?.setFound(found.size, FACTS.length, UNLOCK_AT);
    try { localStorage.setItem(FOUND_KEY, JSON.stringify([...found])); } catch { /* storage unavailable */ }
  }, [found, ready]);

  // Scrolls to where spread j lies open, so the camera and page turn stay in sync with the scroll.
  const goToSpread = (j: number) => {
    const y = spreadScrollY(BOOK_STOP, Math.max(0, Math.min(SPREADS - 1, j)), SPREADS);
    if (y !== null) glideTo(y);
  };

  const fact = FACTS.find(f => f.id === picked);
  const project = spread > 0 ? PROJECTS[spread - 1] : null;
  const locked = found.size < UNLOCK_AT;
  const toUnlock = UNLOCK_AT - found.size;

  return (
    <>
      {loading && <Loader ready={ready} onDone={() => setLoading(false)} />}
      <div ref={hostRef} className="scene" aria-hidden="true" />
      {glNote === 'open' && !loading && <NoWebGL onClose={() => setGlNote(null)} onHide={atTop ? undefined : () => setGlNote('pill')} />}
      <a href="#experience" className={`scroll-hint${atTop && !loading && glNote !== 'open' ? ' is-on' : ''}`} tabIndex={atTop && glNote !== 'open' ? 0 : -1}>
        <span>Scroll</span>
        <svg width="14" height="18" viewBox="0 0 14 18" aria-hidden="true"><path d="M7 1v15M1 10l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2.2" /></svg>
      </a>

      <div ref={bubbleRef} className="bubble" role="status" aria-live="polite">
        {fact && (
          <div key={fact.id} className="bubble-inner">
            <span className="bubble-label">{fact.label}</span>
            <p>{fact.text}</p>
          </div>
        )}
      </div>

      <header className="site-header">
        <a href="#intro" className="brand"><AsteriskMark /><span>Isak Gerre</span></a>
        {glNote === 'pill' && (
          <button type="button" className="nogl-pill" onClick={() => setGlNote('open')}>
            3D off<span className="nogl-pill-more"> · how to fix</span>
          </button>
        )}
        <nav aria-label="Main">
          <div ref={navRef} className="nav-links">
            {NAV.map((n, i) => (
              <a key={n.href} href={n.href} aria-current={i === navAt ? 'location' : undefined}>{n.label}</a>
            ))}
            <span ref={markRef} className="nav-mark" aria-hidden="true" />
          </div>
          <button type="button" className="theme-btn" aria-pressed={dark} onClick={() => setDark(d => !d)}>
            <span className="sr-only">Dark mode</span>
            {dark ? (
              <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M10.5 1.5a6.5 6.5 0 1 0 4 11.6A6 6 0 0 1 10.5 1.5Z" fill="currentColor" /></svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="3.2" fill="currentColor" /><path d="M8 .8v2.4M8 12.8v2.4M.8 8h2.4M12.8 8h2.4M2.9 2.9l1.7 1.7M11.4 11.4l1.7 1.7M2.9 13.1l1.7-1.7M11.4 4.6l1.7-1.7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="square" /></svg>
            )}
          </button>
          <a href={CV} download className="nav-cv">CV ↓</a>
        </nav>
      </header>

      <main>
        {/* Folds up into the header's logo on the way down; the logo takes you back and unfolds it. */}
        <section id="intro" data-stop="0" className="stop stop-intro">
          <Card side={-1} accent="yellow" fold>
            <span className="eyebrow muted">Front-end engineer · Malmö, Sweden</span>
            <div className="intro-head">
              <h1>Isak<br />Gerre</h1>
              <picture>
                <source type="image/webp" srcSet="isak-portrait-320.webp 320w, isak-portrait-480.webp 480w" sizes="clamp(110px, 13vw, 160px)" />
                <img src="isak-portrait.png" alt="Isak Gerre" className="portrait" width={160} height={162} fetchPriority="high" />
              </picture>
            </div>
            <p className="lead">{INTRO}</p>
            <div className="actions">
              <a href={CV} download className="btn btn-solid">Download CV</a>
              <a href={`mailto:${EMAIL}`} className="btn btn-outline">{EMAIL}</a>
            </div>
          </Card>
        </section>

        {/* In screens mode, the next sections are drawn onto the monitor, the laptop and the phone, and hold the camera there a while. */}
        <section id="experience" data-stop="1" data-hold={screens ? '' : undefined} data-center={screens ? '' : undefined} className="stop stop-right stop-wide stop-surface">
          <Card side={1} accent="blue" surface={screens ? 'monitor' : undefined}>
            {screens ? <Computer clock={clock}><Experience /></Computer> : <Experience />}
          </Card>
        </section>
        {/* The sketchbook on the desk turns a page per project as this section scrolls. In screens mode the
            pages carry the whole project and their own page-turn links; the card stays for keyboard use. */}
        <section id="projects" data-stop="2" data-hold data-center={screens ? '' : undefined} className="stop-book" style={{ '--spreads': SPREADS } as React.CSSProperties}>
          <div className="book-rail">
            <Card side={-1} accent="red" className="book-card">
              <div className="book-head" aria-hidden="true">
                <span className="eyebrow">Selected projects</span>
                {project && <span className="book-title">{project.name}</span>}
                <span className="meta muted">{project ? `${String(spread).padStart(2, '0')} / ${String(PROJECTS.length).padStart(2, '0')}` : 'Sketchbook'}</span>
              </div>
              <div key={spread} className="book-page">
                {project ? (
                  <article className="project">
                    <div className="meta muted"><span>{project.org}</span><span>{project.year}</span></div>
                    <h3>{project.name}</h3>
                    <ProjectText text={project.text} />
                    <span className="tags">{project.tags}</span>
                    {project.url && <a href={project.url} target="_blank" rel="noopener" className="visit">Visit {project.url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]} →</a>}
                  </article>
                ) : (
                  <>
                    <h2>Selected projects</h2>
                    <p className="lead">{PROJECTS.length} projects, one per page. Keep scrolling to flip through the sketchbook.</p>
                  </>
                )}
              </div>
              <div className="book-nav">
                <button type="button" onClick={() => goToSpread(spread - 1)} disabled={spread === 0}>
                  <span aria-hidden="true">←</span> {spread === 1 ? 'Contents' : 'Previous'}
                </button>
                {screens && project?.url && (
                  <a href={project.url} target="_blank" rel="noopener" className="book-visit">Visit {project.url.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]} ↗</a>
                )}
                <button type="button" onClick={() => goToSpread(spread + 1)} disabled={spread === SPREADS - 1}>
                  {spread === 0 ? 'First project' : 'Next'} <span aria-hidden="true">→</span>
                </button>
              </div>
            </Card>
          </div>
          <div className="sr-only">
            <h2>Selected projects</h2>
            {PROJECTS.map(p => (
              <article key={p.name}>
                <h3>{p.name}</h3>
                <p>{[p.org, p.year].filter(Boolean).join(', ')}. {p.text} {p.tags}.{p.url && ` ${p.url}`}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="skills" data-stop="3" data-hold={screens ? '' : undefined} data-center={screens ? '' : undefined} className="stop stop-right stop-wide stop-surface">
          <Card side={1} accent="yellow" surface={screens ? 'laptop' : undefined}>
            <SurfaceBar title="~/toolkit" />
            <span className="eyebrow">Skills</span>
            <h2>Toolkit</h2>
            <div className="skills">
              {SKILLS.map(s => (
                <div key={s.label} className="skill">
                  <span className="label">{s.label}</span>
                  <p>{s.text}</p>
                </div>
              ))}
              <div className="skill skill-langs">
                <span className="label">Languages</span>
                <div className="langs">
                  {LANGS.map(l => <span key={l.name}><strong>{l.name}</strong> {l.level}</span>)}
                </div>
              </div>
            </div>
          </Card>
        </section>

        {/* The one stop where the room is to be explored: the camera holds, and the panel sits low and centered
            between the arrows that turn the room and tilt the view. */}
        <section id="fun-facts" data-stop="4" data-hold data-center className="stop-hobbies">
          <div className={`facts-rail${exploring || !has3d ? '' : ' is-away'}`}>
            {has3d && (
              <button type="button" className="turn-btn turn-l" aria-label="Turn the room left" onClick={() => roomRef.current?.turn(-1)} tabIndex={exploring ? 0 : -1}>
                <span aria-hidden="true">↺</span><span className="turn-label">Turn</span>
              </button>
            )}
            <Card side={-1} accent="yellow" className="facts-card" still>
              <div className="facts-head">
                <span className="eyebrow">Fun facts</span>
                <button type="button" className="facts-min" onClick={() => setFactsMin(m => !m)} aria-expanded={!factsMin} aria-controls="facts-body">
                  {factsMin ? 'Show' : 'Hide'}<span aria-hidden="true">{factsMin ? '+' : '–'}</span>
                </button>
              </div>
              <div id="facts-body" className="facts-body" hidden={factsMin}>
                {has3d ? (
                  <>
                    <h2 className="h2-sm">{TOUCH ? 'Tap' : 'Click'} around my room</h2>
                    <p>Most things in here have a story. {TOUCH ? 'Tap' : 'Click'} anything that bounces to hear it. Find {UNLOCK_AT} to unlock the arcade.</p>
                  </>
                ) : (
                  <>
                    <h2 className="h2-sm">A few things about me</h2>
                    <p>Each of these belongs to something in my 3D room, which is switched off in this browser.</p>
                  </>
                )}
              </div>
              {has3d && (
                <div className="found">
                  <div className="found-dots" aria-hidden="true">
                    {FACTS.map((f, i) => <i key={f.id} className={[found.has(f.id) ? 'on' : '', i === UNLOCK_AT - 1 ? 'key' : ''].join(' ')} />)}
                  </div>
                  <span className="meta">
                    {found.size === FACTS.length ? 'All found, nice!' : `${found.size} / ${FACTS.length} found`}
                    {' · '}
                    {locked ? `${toUnlock} more to unlock the arcade` : <a href="#arcade" className="unlocked">Arcade unlocked ▶</a>}
                  </span>
                </div>
              )}
              {has3d ? (
                <details className="facts-list" hidden={factsMin}>
                  <summary>Or read them all as a list</summary>
                  <ul>
                    {FACTS.map(f => (
                      <li key={f.id}>
                        {/* The picked one is marked here too, since its object may be off screen or behind this card. */}
                        <button type="button" aria-pressed={picked === f.id} onClick={() => { pick(f.id); roomRef.current?.select(f.id); }}>
                          <strong>{f.label}</strong> {f.text}
                        </button>
                      </li>
                    ))}
                  </ul>
                </details>
              ) : (
                <ul className="facts-list facts-plain" hidden={factsMin}>
                  {FACTS.map(f => <li key={f.id}><strong>{f.label}</strong> {f.text}</li>)}
                </ul>
              )}
            </Card>
            {has3d && (
              <div className="turn-side">
                <div className="tilt-btns">
                  <button type="button" className="turn-btn tilt-btn" aria-label="Look up" onClick={() => roomRef.current?.tilt(1)} tabIndex={exploring ? 0 : -1}>
                    <span aria-hidden="true">↑</span>
                  </button>
                  <button type="button" className="turn-btn tilt-btn" aria-label="Look down" onClick={() => roomRef.current?.tilt(-1)} tabIndex={exploring ? 0 : -1}>
                    <span aria-hidden="true">↓</span>
                  </button>
                </div>
                <button type="button" className="turn-btn turn-r" aria-label="Turn the room right" onClick={() => roomRef.current?.turn(1)} tabIndex={exploring ? 0 : -1}>
                  <span aria-hidden="true">↻</span><span className="turn-label">Turn</span>
                </button>
              </div>
            )}
          </div>
        </section>

        <section id="contact" data-stop="5" data-hold={screens ? '' : undefined} data-center={screens ? '' : undefined} className="stop stop-contact stop-surface">
          <Card side={-1} accent="blue" surface={screens ? 'phone' : undefined}>
            <div className="phone-status" aria-hidden="true"><span>{clock}</span><span className="phone-notch" /><span>5G ▮▮▮</span></div>
            <span className="eyebrow">Contact</span>
            <h2>Get in touch</h2>
            <div className="contact-list">
              <a href={`mailto:${EMAIL}`}><span className="contact-kind">Email</span><span>{EMAIL}</span><span aria-hidden="true">→</span></a>
              <a href={PHONE.href}><span className="contact-kind">Phone</span><span>{PHONE.label}</span><span aria-hidden="true">→</span></a>
              <a href={LINKEDIN} target="_blank" rel="noopener"><span className="contact-kind">LinkedIn</span><span>Isak Gerre</span><span aria-hidden="true">→</span></a>
            </div>
            <div className="actions"><a href={CV} download className="btn btn-solid">Download CV (PDF)</a></div>
          </Card>
        </section>

        <section id="arcade" data-stop="6" className="stop stop-right stop-arcade">
          <Card side={1} accent="red">
            <span className="eyebrow">Bonus level</span>
            <h2>Isak's arcade</h2>
            <p className="lead">Three mini-games made from my favorite bands, movies and games. How well do you know my taste?</p>
            <ul className={`arcade-games${locked && has3d ? ' is-locked' : ''}`}>
              {GAMES.map(g => <li key={g.id}><strong>{g.title}</strong> {g.group}</li>)}
            </ul>
            {locked && has3d ? (
              <>
                <div key={nudge} className={`lock-note${nudge ? ' is-nudged' : ''}`} role="status">
                  <span className="lock-icon" aria-hidden="true" />
                  <p><strong>Locked.</strong> Find {UNLOCK_AT} fun facts in my room to insert a coin. <span className="meta">{found.size} / {UNLOCK_AT}</span></p>
                </div>
                <div className="actions">
                  <a href="#fun-facts" className="btn btn-solid">Find fun facts ↑</a>
                </div>
              </>
            ) : (
              <div className="actions">
                <button type="button" className="btn btn-start" onClick={openArcade}>Start ▶</button>
              </div>
            )}
          </Card>
        </section>
      </main>

      <footer className="site-footer">
        <div className="foot-stripe" aria-hidden="true"><span /><span /><span /></div>
        <div className="foot-inner">
          <div className="foot-sign">
            <a href="#intro" className="foot-mark" aria-label="Back to the top"><AsteriskMark size={76} /></a>
            <p className="foot-big">Thanks for stopping by my&nbsp;room.</p>
          </div>
          <div className="foot-cols">
            <div>
              <span className="meta">Say hi</span>
              <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
              <a href={PHONE.href}>{PHONE.label}</a>
              <a href={LINKEDIN} target="_blank" rel="noopener">LinkedIn ↗</a>
              <a href={CV} download>CV (PDF) ↓</a>
            </div>
            <div>
              <span className="meta">Around the room</span>
              {NAV.map(n => <a key={n.href} href={n.href}>{n.label}</a>)}
            </div>
          </div>
          <div className="foot-base">
            <span>© {new Date().getFullYear()} Isak Gerre · Malmö, Sweden</span>
            <span>Built with React and Three.js</span>
            <a href="#intro">Back to top ↑</a>
          </div>
        </div>
      </footer>

      {flying && (
        <div className="fly-hud" role="dialog" aria-label="Drone controls">
          <span className="eyebrow">Flying the drone</span>
          {realistic ? (
            <ul>
              <li><kbd>W</kbd><kbd>S</kbd> throttle up, down</li>
              <li><kbd>A</kbd><kbd>D</kbd> yaw left, right</li>
              <li><kbd>↑</kbd><kbd>↓</kbd> pitch forward, back</li>
              <li><kbd>←</kbd><kbd>→</kbd> roll left, right</li>
            </ul>
          ) : (
            <ul>
              <li><kbd>W</kbd><kbd>S</kbd> or <kbd>↑</kbd><kbd>↓</kbd> forward, back</li>
              <li><kbd>A</kbd><kbd>D</kbd> or <kbd>←</kbd><kbd>→</kbd> turn left, right</li>
              <li><kbd>Space</kbd> up · <kbd>Shift</kbd> down</li>
            </ul>
          )}
          {realistic && <ThrottleGauge room={roomRef} />}
          <button type="button" className="fly-mode" aria-pressed={realistic} onClick={e => { setRealistic(r => !r); e.currentTarget.blur(); }}>
            <span className="fly-switch" aria-hidden="true" />Realistic mode
          </button>
          <button type="button" className="btn btn-outline" onClick={() => roomRef.current?.stopFlying()}>Land <kbd>Esc</kbd></button>
          {/* Touch screens get sticks instead of keys: a pad to fly and turn, and up/down on the other thumb. */}
          <div className="fly-pad fly-move">
            <FlyButton room={roomRef} control="f" label="Forward">▲</FlyButton>
            <FlyButton room={roomRef} control="l" label="Turn left">◀</FlyButton>
            <FlyButton room={roomRef} control="r" label="Turn right">▶</FlyButton>
            <FlyButton room={roomRef} control="b" label="Back">▼</FlyButton>
          </div>
          <div className="fly-pad fly-lift">
            <FlyButton room={roomRef} control="u" label="Up">Up</FlyButton>
            <FlyButton room={roomRef} control="d" label="Down">Down</FlyButton>
          </div>
        </div>
      )}
      {cube && (
        <div className="fly-hud cube-hud" role="dialog" aria-label="Rubik's cube">
          <span className="eyebrow">Rubik's cube</span>
          <p className="cube-fact">{FACTS.find(f => f.id === 'cube')?.text} Your turn.</p>
          <ul>
            <li>Drag a face to turn it</li>
            <li>Drag beside the cube, or <kbd>←</kbd><kbd>↑</kbd><kbd>→</kbd><kbd>↓</kbd>, to look around it</li>
            <li>Scroll or pinch to zoom</li>
          </ul>
          {/* The compact phone strip drops the list above; this keeps the one thing you need to know. */}
          <span className="cube-hint">Drag a face to turn it, beside the cube to look around</span>
          <span className="meta cube-count" role="status">
            {cube.solved ? (cube.moves ? `Solved in ${cube.moves} moves!` : 'Solved') : `${cube.moves} ${cube.moves === 1 ? 'move' : 'moves'}`}
          </span>
          <div className="cube-actions">
            <button type="button" className="btn btn-outline" onClick={e => { roomRef.current?.scrambleCube(); e.currentTarget.blur(); }}>Scramble</button>
            <button type="button" className="btn btn-outline" onClick={() => roomRef.current?.stopCube()}>Put down <kbd>Esc</kbd></button>
          </div>
        </div>
      )}
      {arcade && <Arcade onClose={closeArcade} attach={attachScreen} />}
    </>
  );
}
